import React from 'react';
import {
    AggregationAlgorithm,
    LeaderboardMetric,
    TeamSupportDto,
    WorkoutCategory,
    WorkoutTypeV2
} from '@api/ChallengeClient';
import { Select } from '@components/select';
import { DatePicker } from '@components/date-picker';
import { TextField } from '@components/textfield';
import { Button } from '@components/ui/button';
import { METRIC_OPTIONS } from '../../edit-leaderboard-settings/constants';
import { PreviewForm, PreviewType } from '../types';

const TYPE_OPTIONS: { value: PreviewType; label: string }[] = [
    { value: 'COMMUNITY_MEMBERS', label: 'Community · Individuals' },
    { value: 'COMMUNITY_TEAMS', label: 'Community · Teams' },
    { value: 'TEAM_MEMBERS', label: 'Team · Individuals' }
];

const AGGREGATION_OPTIONS = [
    { label: 'Sum', value: AggregationAlgorithm.SUM },
    { label: 'Average', value: AggregationAlgorithm.AVERAGE }
];

const WORKOUT_CATEGORY_OPTIONS = [
    { label: 'Any', value: undefined },
    ...Object.values(WorkoutCategory).map((value) => ({ label: value, value }))
];

const WORKOUT_TYPE_OPTIONS = [
    { label: 'Any', value: undefined },
    ...Object.values(WorkoutTypeV2).map((value) => ({ label: value, value }))
];

interface Props {
    form: PreviewForm;
    onChange(form: PreviewForm): void;
    teams: TeamSupportDto[];
    loading: boolean;
    onRun(): void;
}

function isValid(form: PreviewForm): boolean {
    if (!form.dateFrom || !form.dateTo || form.dateFrom > form.dateTo) {
        return false;
    }
    if (form.type === 'TEAM_MEMBERS' && !form.teamId) {
        return false;
    }
    if (
        form.leaderboardMetric === LeaderboardMetric.REWARDS &&
        !form.currencyCode?.trim()
    ) {
        return false;
    }
    if (
        form.leaderboardMetric === LeaderboardMetric.COMPOSITE_POINTS &&
        !form.compositePointTypeKey?.trim()
    ) {
        return false;
    }
    return true;
}

const toIsoDay = (value: Date | null) => {
    if (!value) {
        return '';
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
};

const DateField = ({
    label,
    value,
    onChange
}: {
    id?: string;
    label: string;
    value: string;
    onChange(value: string): void;
}) => (
    <DatePicker
        label={label}
        clearable
        value={value ? new Date(`${value}T00:00:00`) : null}
        onChange={(next) => onChange(toIsoDay(next))}
    />
);

export const PreviewFormFields = ({
    form,
    onChange,
    teams,
    loading,
    onRun
}: Props) => {
    const set = (patch: Partial<PreviewForm>) =>
        onChange({ ...form, ...patch });

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                <Select
                    label="Leaderboard"
                    value={form.type}
                    options={TYPE_OPTIONS}
                    onChange={(type: PreviewType) =>
                        set({ type, teamId: undefined })
                    }
                />

                {form.type === 'TEAM_MEMBERS' && (
                    <Select
                        label="Team"
                        value={form.teamId}
                        options={teams.map((team) => ({
                            label: team.name,
                            value: team.id
                        }))}
                        onChange={(teamId: number) => set({ teamId })}
                    />
                )}

                <Select
                    label="Metric"
                    value={form.leaderboardMetric}
                    options={METRIC_OPTIONS}
                    onChange={(leaderboardMetric: LeaderboardMetric) =>
                        set({ leaderboardMetric })
                    }
                />

                {form.type === 'COMMUNITY_TEAMS' && (
                    <Select
                        label="Aggregation"
                        value={form.aggregation}
                        options={AGGREGATION_OPTIONS}
                        onChange={(aggregation: AggregationAlgorithm) =>
                            set({ aggregation })
                        }
                    />
                )}

                <DateField
                    id="preview-date-from"
                    label="Date from"
                    value={form.dateFrom}
                    onChange={(dateFrom) => set({ dateFrom })}
                />
                <DateField
                    id="preview-date-to"
                    label="Date to"
                    value={form.dateTo}
                    onChange={(dateTo) => set({ dateTo })}
                />

                <Select
                    label="Workout category"
                    value={form.workoutCategory}
                    options={WORKOUT_CATEGORY_OPTIONS}
                    onChange={(workoutCategory?: WorkoutCategory) =>
                        set({ workoutCategory })
                    }
                />

                <Select
                    label="Workout type"
                    value={form.workoutType}
                    options={WORKOUT_TYPE_OPTIONS}
                    onChange={(workoutType?: WorkoutTypeV2) =>
                        set({ workoutType })
                    }
                />

                {form.leaderboardMetric === LeaderboardMetric.REWARDS && (
                    <TextField
                        type="text"
                        label="Currency code"
                        required
                        value={form.currencyCode ?? ''}
                        onChange={(value) =>
                            set({ currencyCode: String(value) })
                        }
                    />
                )}

                {form.leaderboardMetric ===
                    LeaderboardMetric.COMPOSITE_POINTS && (
                    <TextField
                        type="text"
                        label="Composite point type key"
                        required
                        value={form.compositePointTypeKey ?? ''}
                        onChange={(value) =>
                            set({ compositePointTypeKey: String(value) })
                        }
                    />
                )}
            </div>

            <div>
                <Button disabled={loading || !isValid(form)} onClick={onRun}>
                    Preview
                </Button>
            </div>
        </div>
    );
};
